import React, { useCallback } from 'react';

import { Box, Divider, Stack, Typography } from '@mui/material';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import MetaItem from '../MetaItem';
import PayloadViewer from '../PayloadViewer';
import { metaGridConfig } from '../VendorRequestDetailDrawer/VendorRequestDetailDrawer.config';
import {
  fetchRequestContent,
  fetchResponseContent,
  formatMetadata,
  formatRequestDate,
  getPayloadDownloadName,
} from '../VendorRequestDetailDrawer/VendorRequestDetailDrawer.utils';
import { VendorRequestsTestIds } from '../vendorRequests.testIds';
import VendorRequestStatusLabel from '../VendorRequestStatusLabel';

import {
  itemContentStyles,
  itemDividerStyles,
  itemMetadataPreStyles,
  itemMetadataSectionStyles,
  itemMetaGridStyles,
  itemPayloadSectionStyles,
  itemSummaryMetaStyles,
} from './VendorRequestItem.styles';

const getItemTitle = item =>
  _.get(item, 'operationType') || _.get(item, 'executionType') || 'Request';

const getItemSummaryMeta = item =>
  [_.get(item, 'executionType'), formatRequestDate(_.get(item, 'createdAt'))]
    .filter(Boolean)
    .join(' · ');

const VendorRequestItem = ({ item, defaultExpanded = false }) => {
  const itemId = _.get(item, 'id', null);
  const status = _.get(item, 'status');
  const metadataText = formatMetadata(_.get(item, 'metadata'));
  const errorDetails = _.get(item, 'errorDetails');
  const summaryMeta = getItemSummaryMeta(item);

  const onFetchRequest = useCallback(() => fetchRequestContent(itemId), [itemId]);
  const onFetchResponse = useCallback(() => fetchResponseContent(itemId), [itemId]);

  const summaryContent = (
    <Stack alignItems="center" direction="row" spacing={1}>
      {status && <VendorRequestStatusLabel status={status} />}
      {summaryMeta && (
        <Typography sx={itemSummaryMetaStyles} variant="regular12">
          {summaryMeta}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box data-testid={VendorRequestsTestIds.ITEM_ACCORDION}>
      <AccordionCDS
        additionalSummaryContent={summaryContent}
        className="p-16"
        defaultExpanded={defaultExpanded}
        title={getItemTitle(item)}
        titleVariant="medium14"
      >
        <Box sx={itemContentStyles}>
          <Box sx={itemMetaGridStyles}>
            {metaGridConfig.map(({ id, label, getValue }) => (
              <MetaItem key={id} label={label} value={getValue(item)} />
            ))}
          </Box>
          {errorDetails && <MetaItem label="Error Details" value={errorDetails} />}
          {metadataText && (
            <Box sx={itemMetadataSectionStyles}>
              <Typography color="text.secondary" variant="medium14">
                Metadata
              </Typography>
              <Box component="pre" sx={itemMetadataPreStyles}>
                {metadataText}
              </Box>
            </Box>
          )}
          <Divider sx={itemDividerStyles} />
          <Box sx={itemPayloadSectionStyles}>
            <PayloadViewer
              contentMeta={_.get(item, 'requestContent')}
              dataTestId={VendorRequestsTestIds.REQUEST_DOWNLOAD_BUTTON}
              downloadName={getPayloadDownloadName(item, 'request')}
              key={`request-${itemId}`}
              label="Request Payload"
              revealDataTestId={VendorRequestsTestIds.REQUEST_REVEAL_BUTTON}
              onFetch={onFetchRequest}
            />
            <PayloadViewer
              contentMeta={_.get(item, 'responseContent')}
              dataTestId={VendorRequestsTestIds.RESPONSE_DOWNLOAD_BUTTON}
              downloadName={getPayloadDownloadName(item, 'response')}
              key={`response-${itemId}`}
              label="Response Payload"
              revealDataTestId={VendorRequestsTestIds.RESPONSE_REVEAL_BUTTON}
              onFetch={onFetchResponse}
            />
          </Box>
        </Box>
      </AccordionCDS>
    </Box>
  );
};

VendorRequestItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    operationType: PropTypes.string,
    executionType: PropTypes.string,
    status: PropTypes.string,
    createdAt: PropTypes.string,
    errorDetails: PropTypes.string,
    metadata: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    requestContent: PropTypes.shape({
      type: PropTypes.string,
      hasUnredactedSensitiveData: PropTypes.bool,
    }),
    responseContent: PropTypes.shape({
      type: PropTypes.string,
      hasUnredactedSensitiveData: PropTypes.bool,
    }),
  }).isRequired,
  defaultExpanded: PropTypes.bool,
};

export default VendorRequestItem;
